import { db } from './db';

const WRAPPER_KEY_SETTINGS_KEY = '__webcrypto.refresh_wrapper_key';

interface WrappedToken {
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
}

async function getOrCreateWrapperKey(): Promise<CryptoKey> {
  const existingKeyRecord = await db.settings_kv.get(WRAPPER_KEY_SETTINGS_KEY);

  if (existingKeyRecord?.value && existingKeyRecord.value instanceof CryptoKey) {
    return existingKeyRecord.value;
  }

  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
    'encrypt',
    'decrypt',
  ]);

  await db.settings_kv.put({
    key: WRAPPER_KEY_SETTINGS_KEY,
    value: key,
    updatedAt: Date.now(),
  });

  return key;
}

/**
 * READ-ONLY wrapper-key fetch for the decrypt path. Throws when the key is
 * absent — it MUST NOT regenerate: minting a fresh key on read would overwrite
 * the real one and PERMANENTLY break decryption of the already-stored tokens
 * (turning a transient read into a hard logout). Callers catch + return null.
 */
async function getExistingWrapperKey(): Promise<CryptoKey> {
  const rec = await db.settings_kv.get(WRAPPER_KEY_SETTINGS_KEY);
  if (rec?.value && rec.value instanceof CryptoKey) {
    return rec.value;
  }
  throw new Error('refresh wrapper key missing');
}

export async function wrapRefreshToken(token: string): Promise<WrappedToken> {
  const key = await getOrCreateWrapperKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoder = new TextEncoder();
  const tokenBytes = encoder.encode(token);

  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, tokenBytes);

  return { iv, ciphertext };
}

/**
 * Wrap an arbitrary secret string under the SAME AES-GCM wrapper key used
 * for the auth tokens, returning a JSON-serialisable `{ iv, ciphertext }`
 * shape (number arrays) ready to store in `settings_kv`. Used by the
 * recovery-mnemonic store so a reveal-later flow can decrypt the phrase
 * without ever persisting it in plaintext. Mirrors the on-disk shape of
 * `storeWrappedRefreshToken`.
 */
export async function wrapSecretForStorage(
  secret: string,
): Promise<{ iv: number[]; ciphertext: number[] }> {
  const wrapped = await wrapRefreshToken(secret);
  return {
    iv: Array.from(wrapped.iv),
    ciphertext: Array.from(new Uint8Array(wrapped.ciphertext)),
  };
}

/**
 * Reverse of {@link wrapSecretForStorage}. Returns the plaintext secret,
 * or `null` if the stored bytes can't be decrypted (e.g. the wrapper key
 * is missing). Never throws — callers treat `null` as "unavailable".
 */
export async function unwrapSecretFromStorage(
  stored: { iv: number[]; ciphertext: number[] },
): Promise<string | null> {
  const ivBuffer = new ArrayBuffer(stored.iv.length);
  new Uint8Array(ivBuffer).set(stored.iv);
  const ciphertextBuffer = new ArrayBuffer(stored.ciphertext.length);
  new Uint8Array(ciphertextBuffer).set(stored.ciphertext);
  try {
    return await unwrapRefreshToken({
      iv: new Uint8Array(ivBuffer),
      ciphertext: ciphertextBuffer,
    });
  } catch {
    return null;
  }
}

export async function unwrapRefreshToken(wrapped: WrappedToken): Promise<string> {
  // Decrypt path — never regenerate the key (see getExistingWrapperKey).
  const key = await getExistingWrapperKey();
  const decoder = new TextDecoder();

  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: wrapped.iv as BufferSource },
      key,
      wrapped.ciphertext,
    );

    return decoder.decode(plaintext);
  } catch {
    throw new Error('Failed to unwrap refresh token');
  }
}

export async function storeWrappedRefreshToken(token: string): Promise<void> {
  const wrapped = await wrapRefreshToken(token);

  await db.settings_kv.put({
    key: 'tokens.refresh',
    value: {
      iv: Array.from(wrapped.iv),
      ciphertext: Array.from(new Uint8Array(wrapped.ciphertext)),
    },
    updatedAt: Date.now(),
  });
}

export async function retrieveRefreshToken(): Promise<string | null> {
  const record = await db.settings_kv.get('tokens.refresh');
  if (!record?.value) {
    return null;
  }

  const wrappedData = record.value as {
    iv: number[];
    ciphertext: number[];
  };

  const ivBuffer = new ArrayBuffer(wrappedData.iv.length);
  const ivView = new Uint8Array(ivBuffer);
  ivView.set(wrappedData.iv);

  const ciphertextBuffer = new ArrayBuffer(wrappedData.ciphertext.length);
  const ciphertextView = new Uint8Array(ciphertextBuffer);
  ciphertextView.set(wrappedData.ciphertext);

  const wrapped: WrappedToken = {
    iv: ivView,
    ciphertext: ciphertextBuffer,
  };

  try {
    return await unwrapRefreshToken(wrapped);
  } catch {
    // Decryption failed (e.g. the AES-GCM wrapper key is missing / was
    // regenerated). Return null rather than throwing so the caller's read
    // (Promise.all) doesn't reject — the silent-reauth path can still
    // authenticate off the persisted access token, and a genuinely
    // unrecoverable token simply falls through to the welcome screen.
    return null;
  }
}

export async function clearRefreshToken(): Promise<void> {
  await db.settings_kv.delete('tokens.refresh');
}

/**
 * Raw presence check for persisted credential bytes, INDEPENDENT of whether
 * they can be decrypted on this read. Lets the silent-reauth path tell a
 * transient read failure (record present but the AES-GCM wrapper key was
 * momentarily unreadable / an IDB read hiccuped → must NOT log out, retry) from
 * a genuine absence (no record at all → welcome). See `App.tsx` silent re-auth.
 */
export async function hasStoredCredentialRecord(): Promise<boolean> {
  const [refresh, access] = await Promise.all([
    db.settings_kv.get('tokens.refresh'),
    db.settings_kv.get('tokens.access'),
  ]);
  return Boolean(refresh?.value) || Boolean(access?.value);
}

// ── Access token (year-long JWT) persistence ───────────────────────────
//
// The BE issues a 365-day access token, but the interceptor only kept it
// in memory — so every reload threw it away and called `/v1/auth/refresh`.
// That refresh endpoint is SINGLE-USE (it rotates + invalidates the old
// refresh token), so refreshing on every reload (racing the WS-reconnect
// and the premature first request) burns the token and 401s. Persisting
// the still-valid access token (same AES-GCM wrap as the refresh token)
// lets us SKIP the refresh on reload entirely → no single-use burn →
// stays signed in for the token's full year. See `App.tsx` silent re-auth.

export async function storeWrappedAccessToken(token: string): Promise<void> {
  const wrapped = await wrapRefreshToken(token);
  await db.settings_kv.put({
    key: 'tokens.access',
    value: {
      iv: Array.from(wrapped.iv),
      ciphertext: Array.from(new Uint8Array(wrapped.ciphertext)),
    },
    updatedAt: Date.now(),
  });
}

export async function retrieveAccessToken(): Promise<string | null> {
  const record = await db.settings_kv.get('tokens.access');
  if (!record?.value) return null;
  const wrappedData = record.value as { iv: number[]; ciphertext: number[] };
  const ivBuffer = new ArrayBuffer(wrappedData.iv.length);
  new Uint8Array(ivBuffer).set(wrappedData.iv);
  const ciphertextBuffer = new ArrayBuffer(wrappedData.ciphertext.length);
  new Uint8Array(ciphertextBuffer).set(wrappedData.ciphertext);
  try {
    return await unwrapRefreshToken({ iv: new Uint8Array(ivBuffer), ciphertext: ciphertextBuffer });
  } catch {
    return null;
  }
}

export async function clearStoredAccessToken(): Promise<void> {
  await db.settings_kv.delete('tokens.access');
}

/** Decode a JWT's `exp` (seconds) into epoch-ms, or null if unparseable. */
export function jwtExpiryMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payloadB64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(
      decodeURIComponent(
        atob(payloadB64)
          .split('')
          .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
          .join(''),
      ),
    ) as { exp?: number };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}
